import Api from '@/models/Api'

/**
 * KAN-195 — Delivery dashboard data contract.
 *
 * Single source of truth for the shape returned by the backend endpoint and the
 * fetch helper every widget consumes.
 *
 * ── Backend ────────────────────────────────────────────────────────────────
 *   GET  delivery-dashboards/overview  ->  { message, result: DeliveryOverviewResponse }
 *
 * ── Bucketing rule (backend) ───────────────────────────────────────────────
 *   • Disney = inventory publisher is the Disney publisher company.
 *   • Disney MLB vs Disney Non-MLB is split by the report reporting name (MLB).
 *   • Non-Disney = every non-Disney publisher.
 *   daily_current  = current month through the PREVIOUS day (today excluded).
 *   daily_previous = full previous month (reference overlay).
 *   goal           = monthly delivery goal (config-driven on the backend).
 */

export interface DeliveryDailyPoint {
  date: string // YYYY-MM-DD
  disneymlb: number
  disneynonmlb: number
  nondisney: number
}

export interface DeliveryGroupRow {
  label: string
  disneymlb: number
  disneynonmlb: number
  nondisney: number
  // Only present on by_advertiser rows — used to star approved accounts.
  disney_account_status?: string
}

/** Keys of the grouped (table/pie/bar) sections of the response. */
export type DeliveryGroupingKey =
  | 'by_industry'
  | 'by_sales_channel'
  | 'by_disney_status'
  | 'by_advertiser'

export interface DeliveryOverviewResponse {
  // The reporting month (YYYY-MM) the payload covers.
  month?: string
  goal: number
  daily_current: DeliveryDailyPoint[]
  daily_previous: DeliveryDailyPoint[]
  by_industry: DeliveryGroupRow[]
  by_sales_channel: DeliveryGroupRow[]
  by_disney_status: DeliveryGroupRow[]
  by_advertiser: DeliveryGroupRow[]
}

/** The three delivery buckets, in display order, with their chart colors. */
export const DELIVERY_BUCKETS = [
  { key: 'disneymlb', label: 'MLB OOM', color: '#2563EB' },
  { key: 'disneynonmlb', label: 'Non-MLB OOM', color: '#60A5FA' },
  { key: 'nondisney', label: 'Shifted off Disney', color: '#F59E0B' },
] as const

export type DeliveryBucket = (typeof DELIVERY_BUCKETS)[number]

/** Darker variants used for previous-month reference lines, by bucket key. */
export const DELIVERY_BUCKET_LINE_COLORS: Record<string, string> = {
  disneymlb: '#1E3A8A',
  disneynonmlb: '#3B82F6',
  nondisney: '#B45309',
}

/**
 * Buckets to render given the dashboard-wide Non-Disney toggle
 * (filters.show_nondisney: 'show' | 'hide'; hidden by default).
 */
export const activeDeliveryBuckets = (showNonDisney: boolean): DeliveryBucket[] =>
  DELIVERY_BUCKETS.filter(b => showNonDisney || b.key !== 'nondisney')

// Dedupe only the in-flight burst, keyed per month — when the dashboard mounts
// (or the month changes / Refresh is clicked) all widgets call this within the
// same tick and share one request per month. We deliberately do NOT persist
// past resolution, so Refresh re-fetches. (The backend caches each month's
// response until 5 AM, so the request itself is cheap.)
const inFlight: Record<string, Promise<DeliveryOverviewResponse> | null> = {}

/** True when the dashboard URL carries ?refresh=1 (or true) — handles both
 * history and hash routing. */
const refreshRequestedFromUrl = (): boolean => {
  const { search, hash } = window.location
  const qs = search || (hash.includes('?') ? hash.slice(hash.indexOf('?')) : '')
  const value = new URLSearchParams(qs).get('refresh')
  return value === '1' || value === 'true'
}

/**
 * Fetch the delivery overview for a month (YYYY-MM; backend defaults to the
 * current month when omitted). Forces a backend rebuild (?refresh=1, which
 * overwrites its until-5AM cache for that month) when called with
 * `refresh = true` OR when the dashboard URL carries ?refresh=1. Deduped to one
 * request per month per burst, not persisted past resolution, so the dashboard
 * Refresh button re-fetches.
 */
export const fetchDeliveryOverview = (refresh = false, month = ''): Promise<DeliveryOverviewResponse> => {
  const key = month || 'current'

  if (!inFlight[key]) {
    const force = refresh || refreshRequestedFromUrl()
    const params: any = {}
    if (force) params.refresh = 1
    if (month) params.month = month

    inFlight[key] = new Api()
      .get('delivery-dashboards/overview', params)
      .then((r: any) => r.data.result)
      .finally(() => {
        inFlight[key] = null
      })
  }
  return inFlight[key] as Promise<DeliveryOverviewResponse>
}
